import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { object, string, bool } from 'yup';
import get from 'lodash/get';
import { Row, Col, Alert } from 'react-bootstrap';
import { useIntl } from 'react-intl';
import Translation from '../../../../../components/stateless/Translation';
import withForm from '../../../../../../admin/hocs/withForm';
import SMFormFieldToggle from '../../../../components/stateless/SMFormFieldToggle';
import CheckoutSubmitButton from '../CheckoutSubmitButton';
import CheckoutFormFieldText from '../CheckoutFormFieldText';
import CheckoutFormFieldCreditCard from '../CheckoutFormFieldCreditCard';
import CheckoutFormExpirationDate from '../CheckoutFormExpirationDate';
import {
  TRACK_CLICK_CHECKOUT_STEP_FIVE_SUBMIT,
  TRACK_CHECKOUT_ADD_PAYMENT_INFO,
  TRACK_CHECKOUT_STEP,
} from '../../../../../../src/web/utils/dataLayer';
import hourglass from './hourglass.svg';
import styles from './styles.scss';
import SMTooltip from '../../../../components/stateless/SMTooltip';
import {
  validateCreditCardNumber,
  validateCreditCardSecurityCode,
  validateExpireDate,
} from './validation';

const propTypes = {
  formError: PropTypes.bool.isRequired,
  submitting: PropTypes.bool.isRequired,
  facility: PropTypes.shape().isRequired,
  errorMessage: PropTypes.shape().isRequired,
  receipt: PropTypes.shape({
    totalAmount: PropTypes.number.isRequired,
  }),
};

const defaultProps = {
  receipt: undefined,
};

const validationSchema = object().shape({
  creditCardNumber: string()
    .nullable()
    .when('payOption', {
      is: 'payNow',
      then: string().required('widgets.checkout.creditCardNumber.required'),
    }),
  creditCardExpireMonth: string().nullable()
    .when('payOption', {
      is: 'payNow',
      then: string().required('widgets.checkout.creditCardExpireMonth.required'),
    }),
  creditCardExpireYear: string().nullable()
    .when('payOption', {
      is: 'payNow',
      then: string().required('widgets.checkout.creditCardExpireYear.required'),
    }),
  creditCardSecurityCode: string()
    .nullable()
    .when('payOption', {
      is: 'payNow',
      then: string().required(
        'widgets.checkout.creditCardSecurityCode.required',
      ),
    }),
  autopayEnroll: bool(),
  payOption: string().required('widgets.checkout.payOption.required'),
});

const initialValues = {
  creditCardNumber: '',
  creditCardExpireMonth: '',
  creditCardExpireYear: '',
  creditCardSecurityCode: '',
  autopayEnroll: false,
  payOption: 'payNow',
};

const today = new Date();

const PaymentForm = ({
  handleSubmit,
  submitting,
  formError,
  errorMessage,
  values,
  facility,
  receipt,
  setFieldValue,
}) => {
  const intl = useIntl();
  const [hasTrackedDataLayer, setHasTrackedDataLayer] = useState(false);
  const { settings: { disableReservations } } = facility;

  useEffect(() => {
    if (!hasTrackedDataLayer && facility) {
      setHasTrackedDataLayer(true);
      TRACK_CHECKOUT_STEP(
        get(facility, 'unitGroup'),
        facility,
        'new_booking_flow_step4',
        'payment',
      );
    }
  }, [facility, hasTrackedDataLayer]);

  const payNowClick = rentalType => TRACK_CHECKOUT_ADD_PAYMENT_INFO(
    get(facility, 'unitGroup'),
    facility,
    get(receipt, 'totalAmount'),
    intl,
    rentalType,
  );

  const tooltipCCVTitle = `widgets.checkout.ccvTooltipBody.${values.creditCardNumber.startsWith('3') ? 'amex' : 'default'}`;
  const tooltipCCVTitleText = `Your security code is a ${values.creditCardNumber.startsWith('3')
    ? 'four digit number above the credit card number'
    : 'three digit number on the back of your card'}.`;

  return (
    <form onSubmit={handleSubmit}>
      {formError ? (
        <Alert bsStyle="danger">
          <Translation {...errorMessage} />
        </Alert>
      ) : null}
      {!disableReservations ? (
        <>
          <Row>
            <Col xs={12} md={8}>
              <h5>
                <Translation
                  id="widgets.checkout.payOption"
                  defaultMessage="When would you like to pay?"
                />
              </h5>
            </Col>
          </Row>
          <Row className="border-b">
            <Col xs={6} md={3}>
              <SMFormFieldToggle
                type="radio"
                name="payOption"
                value="payNow"
                label={(
                  <span className={styles.formFieldToggleLabel}>
                    <Translation
                      id="widgets.checkout.payNow"
                      defaultMessage="Pay Now"
                    />
                  </span>
                )}
              />
            </Col>
            <Col xs={6} md={4}>
              <SMFormFieldToggle
                type="radio"
                name="payOption"
                value="payLater"
                label={(
                  <span className={styles.formFieldToggleLabel}>
                    <Translation
                      id="widgets.checkout.payLater"
                      defaultMessage="Pay in Store Later"
                    />
                  </span>
                )}
              />
            </Col>
          </Row>
        </>
      ) : null}
      {values.payOption === 'payLater' ? (
        <div className="mt-3">
          <Translation
            id="widgets.checkout.payLaterInfo"
            defaultMessage="Please come in to the office within 72 hours to make your payment and finalize your rental. We'll call you before your check-in date to make sure everything is ready. We accept cash, credit card, debit, or check."
          />
        </div>
      ) : null}
      {values.payOption === 'payNow' ? (
        <>
          <Row>
            <Col xs={12} md={8}>
              <CheckoutFormFieldCreditCard
                required
                name="creditCardNumber"
                type="tel"
                label={{
                  defaultMessage: 'Credit Card Number',
                  id: 'widgets.checkout.creditCardNumber',
                }}
                autoComplete="cc-number"
                validate={value => validateCreditCardNumber(value)}
                autoCorrect="off"
                currentCountry={facility.address.country}
              />
            </Col>
          </Row>
          <Row>
            <Col xs={6} md={4}>
              <CheckoutFormExpirationDate
                setFieldValue={setFieldValue}
                validate={() => (
                  validateExpireDate(
                    values.creditCardExpireMonth,
                    values.creditCardExpireYear,
                    today,
                  )
                )}
              />
            </Col>
            <Col xs={6} md={3}>
              <div style={{ position: 'relative' }}>
                <CheckoutFormFieldText
                  required
                  name="creditCardSecurityCode"
                  style={{ paddingRight: '44px' }}
                  type="tel"
                  maxLength={4}
                  label={{
                    defaultMessage: 'Security Code',
                    id: 'widgets.checkout.creditCardSecurityCode',
                  }}
                  autoComplete="cc-csc"
                  autoCorrect="off"
                  validate={value => validateCreditCardSecurityCode(value)}
                />
                <div
                  style={{
                    position: 'absolute',
                    right: '2px',
                    padding: '12px',
                  }}
                  className={styles.tooltipCCV}
                >
                  <SMTooltip
                    title={(
                      <Translation
                        id="widgets.checkout.ccvTooltipTitle"
                        defaultMessage="Locate Your Security Code"
                      />
                    )}
                    body={(
                      <Translation
                        id={tooltipCCVTitle}
                        defaultMessage={tooltipCCVTitleText}
                      />
                    )}
                    className={`${styles.tooltip}`}
                  />
                </div>
              </div>
            </Col>
          </Row>
          <Row>
            <Col xs={12}>
              <SMFormFieldToggle
                type="checkbox"
                name="autopayEnroll"
                value
                label={(
                  <Translation
                    id="widgets.checkout.autopayEnroll"
                    defaultMessage="Enroll in autopay program to charge my card on the 1st of every month"
                  />
                )}
              />
            </Col>
          </Row>
        </>
      ) : null}
      <Row>
        <Col xs={12}>
          {submitting ? (
            <img
              src={hourglass}
              className={styles.hourglass}
              alt="Payment loading"
            />
          ) : null}
          {values.payOption === 'payLater' ? (
            <CheckoutSubmitButton
              submitLabel={{
                id: 'widgets.checkout.payButton.submit.payLater',
                defaultMessage: 'Pay Later and Continue',
              }}
              submitting={submitting}
              submittingLabel={{
                id: 'widgets.checkout.payButton.submitting.payLater',
                defaultMessage: 'Continuing...',
              }}
              onClick={() => payNowClick('RIN0')}
              {...TRACK_CLICK_CHECKOUT_STEP_FIVE_SUBMIT}
            />
          ) : null}
          {values.payOption === 'payNow' ? (
            <CheckoutSubmitButton
              submitLabel={{
                id: 'widgets.checkout.payButton.submit',
                defaultMessage: 'Pay Now and Continue',
              }}
              submitting={submitting}
              submittingLabel={{
                id: 'widgets.checkout.payButton.submitting',
                defaultMessage: 'Paying...',
              }}
              onClick={() => payNowClick('RIN')}
              {...TRACK_CLICK_CHECKOUT_STEP_FIVE_SUBMIT}
            />
          ) : null}
        </Col>
      </Row>
    </form>
  );
};

PaymentForm.propTypes = propTypes;
PaymentForm.defaultProps = defaultProps;

export default withForm({
  mapPropsToValues: () => {
    const formattedInitialValues = initialValues;

    return formattedInitialValues;
  },
  validationSchema,
  handleSubmit: (formPayload, formikBag) => formikBag.props.onSubmit(
    formPayload,
    formikBag.props.currentStep,
    formikBag.props.nextStep,
    formikBag.props.scrollToRef,
  ),
})(PaymentForm);
